import React, { useEffect, useState, useRef } from 'react';
import moment from 'moment';
import { Components } from 'versafleet-core';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useGetOrderDetails } from 'queryActions/useOrderDetails';
import { useRescheduleTask, useGetTimeWindowsAvailability } from 'queryActions/useTasksRescheduling';
import Footer from 'modules/footer';

function EditTasksRescheduling() {
  const [baseDate, setBaseDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedTime, setSelectedTime] = useState(null);
  const [errors, setErrors] = useState([]);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const initializedFromOrderDetails = useRef(false);

  const { orderDetails } = useGetOrderDetails();
  const {
    rescheduleTask,
    isLoadingRescheduleTask,
    errorRescheduleTask,
    resetRescheduleTask,
  } = useRescheduleTask();
  const {
    timeWindowsAvailability,
    getTimeWindowsAvailability,
    isLoadingTimeWindowsAvailability,
    errorTimeWindowsAvailability,
  } = useGetTimeWindowsAvailability();

  const maxReschedulingDays = orderDetails?.account
    .tracking_setting.max_rescheduling_days || null;

  const location = useLocation();
  const navigate = useNavigate();
  const { detail, domain } = useParams();

  // If there are specified time windows
  // but there are none available on the selected date prevent submitting the form
  const disableActionPanel = timeWindowsAvailability[0]?.has_time_windows
    && timeWindowsAvailability[0]?.data.length === 0;

  // Prevent unverified users from accessing this page
  useEffect(() => {
    if (!location.state?.fromVerifyPage) {
      navigate(`../${domain}/${detail}`, { replace: true });
    }
  }, [location.state, domain, detail, navigate]);

  useEffect(() => {
    if (orderDetails) {
      const matchedTimeWindow = timeWindowsAvailability[0]?.data
        .find(time => time.time_window_id === orderDetails.time_window_id);
      setSelectedTime(matchedTimeWindow?.time_window_id
        || timeWindowsAvailability[0]?.data[0]?.time_window_id);
    }
  }, [orderDetails, timeWindowsAvailability]);

  useEffect(() => {
    if (orderDetails && !initializedFromOrderDetails.current) {
      setSelectedDate(orderDetails.time_to);
      setBaseDate(orderDetails.time_to);
      initializedFromOrderDetails.current = true;
    }
  }, [orderDetails]);

  useEffect(() => {
    getTimeWindowsAvailability(selectedDate);
  }, [selectedDate, getTimeWindowsAvailability]);

  useEffect(() => {
    if (errorRescheduleTask || errorTimeWindowsAvailability) {
      setErrors([errorRescheduleTask || errorTimeWindowsAvailability]);
    }
  }, [errorRescheduleTask, errorTimeWindowsAvailability]);

  function handleDateChange(date) {
    setErrors([]);
    resetRescheduleTask();

    setSelectedDate(date);
    getTimeWindowsAvailability(date);
  }

  async function handleRescheduleTask() {
    await rescheduleTask({ scheduled_date: selectedDate, time_window_id: selectedTime });

    if (!errorRescheduleTask) {
      setShowConfirmationModal(true);
    }
  }

  return (
    <div className="vf-tracking">
      <Components.Container
        className="vf-rescheduling__container"
      >
        <div>
          <Components.Window.Header>
            <Components.Window.Header.Title>
              Choose a new date and time
            </Components.Window.Header.Title>
            <Components.Window.Description>
              Select a new date, time, or both from the available options below.
            </Components.Window.Description>
          </Components.Window.Header>

          <Components.Window.FieldEdit.DatePicker
            description="Select a new date for your order."
            fieldName="Date"
            fromDate={moment(baseDate)}
            maxDate={moment(baseDate).add(maxReschedulingDays - 1, 'days').endOf('day').toDate()}
            onChange={value => handleDateChange(value)}
            value={selectedDate}
          />

          {timeWindowsAvailability[0]?.has_time_windows
            && (
              <Components.Window.Subsection>
                <Components.Window.Subsection.Heading>
                  Available Time Windows
                </Components.Window.Subsection.Heading>
                <Components.Window.Description>
                  Select from the available time windows below for your order.
                </Components.Window.Description>
              </Components.Window.Subsection>
            )}

          <Components.Window.Subsection>
            {isLoadingTimeWindowsAvailability && <Components.EllipsisLoading />}
          </Components.Window.Subsection>

          {timeWindowsAvailability?.[0]
            && !isLoadingTimeWindowsAvailability && (
              <Components.Window.Subsection>
                {timeWindowsAvailability[0].data.length === 0 ? (
                  // If there are specified time windows
                  // but there are none available on the selected date
                  <Components.Window.Content>
                    {timeWindowsAvailability[0].has_time_windows && 'No available time slots on this date'}
                  </Components.Window.Content>
                ) : (
                  <Components.Input.Radio onChange={setSelectedTime} value={selectedTime}>
                    {timeWindowsAvailability[0].data.map(item => (
                      <Components.Input.Radio.Item
                        key={item.time_window_id}
                        value={item.time_window_id}
                      >
                        {item.time_window_name}
                        <Components.Window.Description>
                          {item.time_range}
                        </Components.Window.Description>
                      </Components.Input.Radio.Item>
                    ))}
                  </Components.Input.Radio>
                )}
              </Components.Window.Subsection>
          )}

          <Components.Window.Subsection>
            <Components.Error errors={errors} />
          </Components.Window.Subsection>
        </div>

        <div className="vf-rescheduling__container-panel">
          <Components.Window.ActionPanel>
            <Components.Window.ActionPanel.Button.Primary
              disabled={disableActionPanel
                || isLoadingTimeWindowsAvailability
                || errors.length > 0}
              fillColor
              loading={isLoadingRescheduleTask}
              onClick={() => handleRescheduleTask()}
            >
              Save new date & time
            </Components.Window.ActionPanel.Button.Primary>
          </Components.Window.ActionPanel>
        </div>

        <Components.Modal.Confirmation
          headerText="The new date & time has been confirmed"
          rightButtonOnClick={() => navigate(`../${domain}/${detail}`, { replace: true })}
          rightButtonText="Continue"
          visible={showConfirmationModal}
        >
          <Components.Window.Description>
            Your order has been rescheduled to
          </Components.Window.Description>
          {`${moment(selectedDate).format('DD MMMM YYYY')}`}
          {' '}
          {timeWindowsAvailability[0]?.data && `at ${timeWindowsAvailability[0]?.data.find(item => item.time_window_id === selectedTime)?.time_range}`}
        </Components.Modal.Confirmation>
      </Components.Container>

      <Footer />
    </div>
  );
}

export default EditTasksRescheduling;
